import { Node } from 'reactflow';
import { TreeNode } from './types';

const VERTICAL_MARGIN = 60;
const HORIZONTAL_MARGIN = 80;

/**
 * Represents a column in the flow. A column can be considered the outbounding
 * box that wraps around a single branch. It is considered to be the end of the
 * branch when the final node has been branched from, i.e. it has more than one
 * child.
 */
type Column = {
  nodes: Node[];
  /**
   * X/Y coordinates of the column. The X/Y coordinates are positioned at the
   * top-left of the column.
   */
  position: { y: number; x: number };
  /**
   * The width of a column is equal to their widest child.
   */
  width: number;
  /**
   * The height of the column. The height is the sum of the child nodes + any
   * margins.
   */
  height: number;
  row?: Row;
};

/**
 * Represents a row in the flow. A row can be considered the outbounding box
 * that wraps nodes that have been branched from a node.
 */
type Row = {
  columns: Column[];
  /**
   * The width of the row. The width is the sum of the child nodes + any margins.
   */
  width: number;
  position: { y: number; x: number };
};

/**
 * Converts a tree hierarchy into rows and columns.
 *
 * @param tree The tree hierarchy this is going to be converted.
 * @returns Column
 */
export const convertToRowsAndColumns = (tree: TreeNode): Column => {
  const column: Column = {
    nodes: [tree.data],
    width: tree.size.width,
    height: tree.size.height,
    position: { x: 0, y: 0 },
    row: undefined,
  };

  const getNodesInColumn = (tree: TreeNode): TreeNode[] => {
    if (tree.children.length > 1) {
      return [];
    }

    return [...tree.children, ...tree.children.flatMap(getNodesInColumn)];
  };

  const nodesInColumn = getNodesInColumn(tree);
  for (const node of nodesInColumn) {
    column.nodes.push(node.data);

    if (node.size.width > column.width) {
      column.width = node.size.width;
    }

    column.height += node.size.height;

    if (node.children.length > 1) {
      const columns = node.children.map(convertToRowsAndColumns);
      const width = columns.reduce((acc, c) => (acc += c.width), 0) + HORIZONTAL_MARGIN * (columns.length - 1);

      column.row = {
        columns: columns,
        width,
        position: { x: 0, y: 0 },
      };
    }
  }

  // Need to account for margins between each node
  column.height += VERTICAL_MARGIN * column.nodes.length;

  return column;
};

/**
 * Set the positions of the cols and rows based on their sizes and any margins that need to be applied.
 *
 * This is a recursive function and will finish once all of the columns and rows have been set.
 *
 * @param widthSoFar is used to position a column based on how much width has been used in the row. E.G:
 * a row of 600px with three columns, and each column is 200px wide, and the first two children have been
 * set, then there will be a remaining 200px left in the row.
 *
 * @param position is used as the starting point of the column, and will either be the initial position (0,0)
 * or the last column.
 *
 * @param col is the current column being set.
 */
const positionColumnsAndRows = (widthSoFar: number, position: { x: number; y: number }, col: Column) => {
  const setX = () => {
    if (widthSoFar) {
      // `widthSoFar` positions the current column based on the remaining space in the row.
      return position.x + widthSoFar;
    }

    return position.x;
  };

  col.position = {
    y: position.y,
    x: setX(),
  };

  if (col.row) {
    col.row.position = {
      // Coordinates are positioned at the top-left of a colum, so we need to move the Y coordinate
      // to the bottom-left of the col to find the row's Y.
      y: col.position.y + col.height,
      // The row's X coordinate will start at the column's, but the row needs to be positioned in
      // the middle of the column. We do that by finding the difference between the row and colum
      // width, and then alter the X coordinate by halving the difference. This should be fine to do
      // as the row will always be wider than the column.
      x: col.position.x - (col.row.width - col.width) / 2,
    };

    // Start recursion and keep track of the width used in the row.
    let widthUsedInRow = 0;
    for (const column of col.row.columns) {
      // The starting position for the columns will be the row's X/Y coordinates.
      // Columns that belong to a row should always be positioned relative to
      // the row.
      positionColumnsAndRows(widthUsedInRow, col.row?.position, column);
      widthUsedInRow += column.width + HORIZONTAL_MARGIN;
    }
  }
};

export const layout = (tree: TreeNode) => {
  /*
    The layout is split out into three distinct parts:

    1) We first convert the Node data structure into columns and rows which allows us
    to easily track the height and width of columns and rows. 

    2) Secondly, we position the column and rows based on their heights and where they
    should be in the hierarchy. Rows position their columns relative to the row. Margins 
    are also included in this calculation.

    3) Finally, we position the nodes relative to their columns. The root node always uses
    the X/Y coordinates of the column, and then all subsequent nodes use the coordinates of
    the previous node.
  */
  const column = convertToRowsAndColumns(tree);
  positionColumnsAndRows(0, { x: 0, y: 0 }, column);

  const setPositionOfNodes = (col: Column) => {
    for (let i = 0; i < col.nodes.length; i++) {
      const currentNode = col.nodes[i];

      // Finds the middle point of the colum and uses that as the starting position for X.
      // To set the node centrally the current node is shifted left by halving it's width.
      const setX = () => (col.position.x ?? 0) + ((col.width ?? 0) / 2 - (currentNode.width ?? 0) / 2);

      // If this is the root node then we always position it relative to
      // the column.
      if (i === 0) {
        currentNode.position = {
          y: col.position.y ?? 0,
          x: setX(),
        };
      }
      // else if this isn't the root node then we position it relative to
      // the previous node.
      else {
        const previousNode = col.nodes[i - 1];
        currentNode.position = {
          y: (previousNode.position.y ?? 0) + (previousNode.height ?? 0) + VERTICAL_MARGIN,
          x: setX(),
        };
      }
    }

    // Recurse through the rows and set their column's nodes.
    col.row?.columns.forEach(setPositionOfNodes);
  };

  // Starts the recursion of the columns.
  setPositionOfNodes(column);

  // convert back to a list of nodes
  const getAllNodes = (col: Column): Node[] => {
    const nodes = col.nodes;
    const childNodes = col.row?.columns.flatMap(getAllNodes) ?? [];

    return [...nodes, ...childNodes];
  };

  return getAllNodes(column);
};
